import { Component, Event, EventEmitter, h, Method, Prop, State, Watch } from "@stencil/core"
import LanguageHandler from "@payair/e-com-common-sdk/core/LanguageHandler"
import { CountryCallingCode } from "../../countries/CountryCallingCode.model"
import { getCountryCallingCodes } from "../../countries/CountryCallingCodes"
import { isConsumerPhoneNumberValid } from "../../helpers/ConsumerInputValidation"
import { reconcileControlledValue } from "../../helpers/reconcileControlledValue"
import { getSupportedLocaleData, getLanguageHandler } from "../../languageHandler/LanguageHandler"
import inputErrorIcon from "../../styles/inputErrorIcon.svg"
import { ConsumerPhoneNumberValue } from "./ConsumerPhoneNumberValue.model"

let phoneNumberInputId = 0

const EMPTY_PHONE_NUMBER: ConsumerPhoneNumberValue = { countryCode: "", phoneNumber: "" }
const normalizeCountryCode = (value: string): string => {
  const trimmedValue = value.trim()
  const withoutInternationalPrefix = trimmedValue.indexOf("00") === 0 ? trimmedValue.slice(2) : trimmedValue
  return withoutInternationalPrefix.replace(/\D/g, "")
}
const normalizeSubscriberNumber = (value: string): string => value.replace(/\D/g, "").slice(0, 14)

/** Controlled phone-number input. */
@Component({
  tag: "phone-number-input",
  styleUrl: "PhoneNumberInput.scss",
  shadow: true,
})
export class PhoneNumberInput {
  /**
   * Merchant site preferred locale. Format based on ISO format for language (ISO 639-1) and country code
   * (ISO 3166-1 alpha-2). The language and country should be separated using an underscore ( _ ).
   * <br /><br />
   * **Supported locale**: en_GB, sv_SE, pl_PL
   * <br /><br />
   * @default en_GB
   */
  @Prop() locale?: string

  /** Current controlled phone-number value. */
  @Prop() value: ConsumerPhoneNumberValue = { ...EMPTY_PHONE_NUMBER }

  /** Whether the input is required. */
  @Prop() required?: boolean

  /** Whether both controls are disabled. */
  @Prop() disabled?: boolean

  /** Whether invalid non-empty values display a validation message. */
  @Prop() showValidationError: boolean = true

  /** Whether the input should be rendered in its externally controlled invalid state. */
  @Prop() invalid?: boolean

  /** Translation key used for an externally controlled validation message. */
  @Prop() errorMessageKey?: string

  /** Fully translated validation message. Takes precedence over errorMessageKey. */
  @Prop() errorMessage?: string

  /** Emitted whenever the user edits either phone-number part. */
  @Event() valueChange: EventEmitter<ConsumerPhoneNumberValue>

  /** Emitted when focus leaves both phone-number controls. */
  @Event() fieldBlur: EventEmitter<void>

  /** Emitted when Enter is pressed in the phone-number text input. */
  @Event() fieldSubmit: EventEmitter<void>

  @State() phoneCodes: CountryCallingCode[] = []
  @State() selectedCountryIsoCode?: string

  private languageHandler: LanguageHandler
  private readonly selectId = `phone-country-code-${phoneNumberInputId}`
  private readonly inputId = `phone-number-${phoneNumberInputId++}`
  private countrySelectElement?: HTMLSelectElement
  private phoneInputElement?: HTMLInputElement

  componentWillLoad() {
    const localeData = getSupportedLocaleData(this.locale)
    this.languageHandler = getLanguageHandler(localeData.lang)
    this.phoneCodes = getCountryCallingCodes(localeData.lang)
    this.reconcileSelectedCountry(this.getDisplayValue())
  }

  @Watch("value")
  updateControlledValue(value: ConsumerPhoneNumberValue) {
    this.reconcileSelectedCountry(this.resolveValue(value))
  }

  private resolveValue(value?: ConsumerPhoneNumberValue): ConsumerPhoneNumberValue {
    const countryCode = normalizeCountryCode(value?.countryCode || "")
    const phoneNumber = normalizeSubscriberNumber(value?.phoneNumber || "")
    return { countryCode, phoneNumber }
  }

  private inferCountryIsoCode(countryCode: string): string | undefined {
    const matchingCountries = this.phoneCodes.filter(option => option.callingCode === countryCode)
    return matchingCountries.length === 1 ? matchingCountries[0].countryCode : undefined
  }

  private reconcileSelectedCountry(value: ConsumerPhoneNumberValue) {
    const selectedCountry = this.phoneCodes.find(
      option => option.countryCode === this.selectedCountryIsoCode && option.callingCode === value.countryCode
    )

    if (!selectedCountry) {
      this.selectedCountryIsoCode = this.inferCountryIsoCode(value.countryCode)
    }
  }

  private getDisplayValue(): ConsumerPhoneNumberValue {
    return this.resolveValue(this.value)
  }

  private emitValue(value: ConsumerPhoneNumberValue) {
    const normalizedValue = this.resolveValue(value)

    this.valueChange.emit({ ...normalizedValue })
  }

  private phoneNumberOnInput = (event: InputEvent) => {
    const input = event.target as HTMLInputElement
    const phoneNumber = normalizeSubscriberNumber(input.value)
    this.emitValue({ ...this.getDisplayValue(), phoneNumber })

    if (!event.isComposing) {
      reconcileControlledValue(input, () => this.getDisplayValue().phoneNumber)
    }
  }

  private phoneNumberOnCompositionEnd = (event: CompositionEvent) => {
    reconcileControlledValue(event.target as HTMLInputElement, () => this.getDisplayValue().phoneNumber)
  }

  private countryCodeOnChange = (event: Event) => {
    const select = event.target as HTMLSelectElement
    const selectedCountry = this.phoneCodes.find(option => option.countryCode === select.value)

    if (!selectedCountry) {
      reconcileControlledValue(select, () => this.selectedCountryIsoCode || "")
      return
    }

    this.selectedCountryIsoCode = selectedCountry.countryCode
    this.emitValue({
      ...this.getDisplayValue(),
      countryCode: selectedCountry.callingCode,
    })

    reconcileControlledValue(select, () => {
      this.reconcileSelectedCountry(this.getDisplayValue())
      return this.selectedCountryIsoCode || ""
    })
  }

  private onFocusOut = (event: FocusEvent) => {
    const field = event.currentTarget as HTMLElement
    const nextFocusedElement = event.relatedTarget as Node | null
    if (!nextFocusedElement || !field.contains(nextFocusedElement)) {
      this.fieldBlur.emit()
    }
  }

  private onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Enter" && !event.isComposing && !event.defaultPrevented && !event.repeat && !this.disabled) {
      event.preventDefault()
      this.fieldSubmit.emit()
    }
  }

  private isValid(value: ConsumerPhoneNumberValue): boolean {
    return isConsumerPhoneNumberValid(value)
  }

  /** Focuses the first incomplete native phone-number control. */
  @Method()
  async focusInput() {
    const value = this.getDisplayValue()
    if (!value.countryCode) {
      this.countrySelectElement?.focus()
    } else {
      this.phoneInputElement?.focus()
    }
  }

  render() {
    const displayValue = this.getDisplayValue()
    const selectedCountry = this.phoneCodes.find(option => option.countryCode === this.selectedCountryIsoCode)
    const hasValue = Boolean(displayValue.countryCode || displayValue.phoneNumber)
    const formatInvalid = this.showValidationError && hasValue && !this.isValid(displayValue)
    const invalid = this.invalid === true || formatInvalid
    const errorMessageKey = this.invalid === true ? this.errorMessageKey || "requiredField" : "wrongFormat"
    const errorMessage = this.errorMessage || this.languageHandler.getString(errorMessageKey)
    const errorId = `${this.inputId}-error`

    return (
      <div class="phoneNumberField">
        <label class="inputLabel" htmlFor={this.inputId}>
          {this.languageHandler.getString("phoneNumber")}
        </label>
        <div
          class="phoneNumberInput"
          aria-invalid={invalid ? "true" : "false"}
          aria-disabled={this.disabled ? "true" : "false"}
          onFocusout={this.onFocusOut}>
          <div class={{ phoneCountrySelector: true, disabled: Boolean(this.disabled) }}>
            <select
              ref={element => (this.countrySelectElement = element)}
              id={this.selectId}
              name="countryCode"
              aria-label={this.languageHandler.getString("phoneCountryCode")}
              aria-required={this.required ? "true" : "false"}
              onChange={this.countryCodeOnChange}
              disabled={this.disabled}
              required={this.required && !displayValue.countryCode}>
              <option value="" disabled hidden selected={!this.selectedCountryIsoCode}>
                {displayValue.countryCode
                  ? `+${displayValue.countryCode}`
                  : this.languageHandler.getString("phoneCountryCode")}
              </option>
              {this.phoneCodes.map(phoneCode => (
                <option
                  value={phoneCode.countryCode}
                  key={phoneCode.countryCode}
                  selected={phoneCode.countryCode === this.selectedCountryIsoCode}>
                  {phoneCode.name} {phoneCode.emoji} (+{phoneCode.callingCode})
                </option>
              ))}
            </select>
            <div class="selectedPhoneCountry" aria-hidden="true">
              <span class="selectedCountryFlag">{selectedCountry?.emoji || "🌐"}</span>
              {displayValue.countryCode && <span class="selectedCallingCode">+{displayValue.countryCode}</span>}
              <span class="phoneCountryChevron" />
            </div>
          </div>
          <input
            ref={element => (this.phoneInputElement = element)}
            id={this.inputId}
            type="tel"
            inputmode="numeric"
            autocomplete="tel-national"
            placeholder={this.languageHandler.getString("phoneNumber")}
            value={displayValue.phoneNumber}
            onInput={this.phoneNumberOnInput}
            onCompositionend={this.phoneNumberOnCompositionEnd}
            onKeyDown={this.onKeyDown}
            disabled={this.disabled}
            required={this.required}
            aria-invalid={invalid ? "true" : "false"}
            aria-describedby={invalid ? errorId : undefined}
          />
          {invalid && <img class="errorIcon" src={inputErrorIcon} alt="" aria-hidden="true" />}
        </div>
        {invalid && (
          <div id={errorId} class="errorInfo" role="alert">
            {errorMessage}
          </div>
        )}
      </div>
    )
  }
}
